/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useContext, useState } from "react";
import axios from "axios";
import { NavLink, withRouter } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import context from "../../../../../../context/auth-context";
import errorImage from "../../../../../../assets/images/image-error.png";
import errorHandler from "../../../../../../hoc/ErrorHandler";
import preloader from "../../../../../../assets/images/preloader.gif";

// Import Material-UI components for the modal
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Paper from "@mui/material/Paper";

const UserProfile = (props) => {
  const authContext = useContext(context);
  const dispatch = useDispatch();
  
  // AI Toggle state
  const [isAiEnabled, setIsAiEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [initialized, setInitialized] = useState(false);
  const [scope, setScope] = useState("company");
  const [userSettingsEnabled, setUserSettingsEnabled] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  useEffect(() => {
    if (!authContext.authenticated) props.history.replace({ pathname: "/" });
    const getUserAvatar = async () => {
      const response = await axios.get(
        `/users/${authContext.userId}/account/avatar`,
        {
          headers: {
            "Company-Code": authContext.companyCode,
          },
          responseType: "blob",
        }
      );

      if (response) {
        response.data instanceof Blob
          ? props.onSetAvatarURI(response.data)
          : console.log("NO DATA");
      } else {
        props.onSetAvatarURIError();
      }
    };
    const getUserGroups = async () => {
      const groups = await axios.get(
        `/users/${authContext.userId}/account/groups`,
        {
          headers: {
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
          },
        }
      );

      if (groups) {
        return props.onInstantiateGroups(groups.data);
      } else {
        return props.onInstantiateGroupsError();
      }
    };

    getUserAvatar();
    getUserGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext.authenticated, authContext.companyId]);

  const showMessage = (msg, type = "success") => {
    setSnackbarMessage(msg);
    setSnackbarType(type);
    setShowSnackbar(true);
    setTimeout(() => setShowSnackbar(false), 3000);
  };

  // Fetch AI status when modal opens
  useEffect(() => {
    const fetchStatus = async () => {
      if (aiModalOpen && !initialized && authContext.accessToken) {
        try {
          console.log("Fetching AI status...");
          const params = authContext.userId && userSettingsEnabled 
            ? { user_id: authContext.userId } 
            : {};
            
          const response = await axios.get("/ai/usage-control", {
            headers: {
              Authorization: authContext.accessToken,
              "Company-Code": authContext.companyCode,
              "Company-Id": authContext.companyId
            },
            params: params
          });
          
          console.log("GET Response:", response.data);

          if (response.data && typeof response.data.enabled === 'boolean') {
            setIsAiEnabled(response.data.enabled);
            setScope(response.data.scope || "company");
            setInitialized(true);
            console.log("Set AI enabled to:", response.data.enabled, "Scope:", response.data.scope);
          } else {
            console.error("Invalid response format:", response.data);
            setInitialized(true);
          }
        } catch (error) {
          console.error("Failed to fetch AI status:", error);
          setInitialized(true);
          showMessage("Error fetching AI status", "error");
        }
      }
    };

    fetchStatus();
  }, [aiModalOpen, initialized, authContext, userSettingsEnabled]);

  const handleToggle = async () => {
    if (!authContext.accessToken) {
      showMessage("Authentication required", "error");
      return;
    }

    const newState = !isAiEnabled;
    setLoading(true);
    setSnackbarMessage("");

    try {
      //console.log("Toggling AI to:", newState);
      
      const requestData = {
        enabled: newState, 
      };
      
      // Add user_id to request if user settings are enabled
      if (authContext.userId && userSettingsEnabled) {
        requestData.user_id = authContext.userId;
      }
      
      const response = await axios.post(
        "/ai/usage-control",
        requestData,
        {
          headers: {
            Authorization: authContext.accessToken,
            "Company-Code": authContext.companyCode,
            "Company-Id": authContext.companyId,
            "Content-Type": "application/json",
          },
        }
      );

     // console.log("POST Response:", response.data);

      if (response.data && typeof response.data.enabled === 'boolean') {
        setIsAiEnabled(response.data.enabled);
        setScope(response.data.scope || (userSettingsEnabled ? "user" : "company"));
        showMessage(response.data.message || "AI usage updated successfully", "success");
        //console.log("Updated AI enabled to:", response.data.enabled);
      } else {
        // Fallback if response format is unexpected
        setIsAiEnabled(newState);
        showMessage("AI mode " + (newState ? "enabled" : "disabled"), "success");
        //console.log("Fallback update to:", newState);
      }
    } catch (error) {
      console.error("Update error:", error);
      
      if (error.response) {
        console.error("Error response:", error.response.data);
        showMessage(error.response.data?.message || `Error: ${error.response.status}`, "error");
      } else if (error.request) {
        console.error("No response received:", error.request);
        showMessage("No response from server", "error");
      } else {
        console.error("Request setup error:", error.message);
        showMessage("Request failed: " + error.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserSettingsToggle = (event) => {
    setUserSettingsEnabled(event.target.checked);
    setInitialized(false); // Reset to fetch new settings
  };

  const openAiModal = () => {
    setAiModalOpen(true);
    setInitialized(false); // Reset to fetch fresh data when modal opens
  };

  const closeAiModal = () => {
    setAiModalOpen(false);
  };

  const userSignOutHandler = () => {
    axios
      .post(`/auth/logout`, { userName: authContext.loginName })
      .then((res) => {
        dispatch({ type: "REMOVE_LOADING" });
        if (res) {
          props.onClearGeoInfo();
          props.onClearTimeZones();
          props.onClearCompanies();
          props.onSignOut();
          props.history.replace({ pathname: "/" });
        }
      })
      .catch((err) => {
        alert("Cannot Log Out, Please Try Again");
      });
  };

  const fl = !props.avatarURI.error
    ? props.avatarURI.URI ?? preloader
    : errorImage;

  // Modal style
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 1,
    outline: 'none'
  };

  return (
    <>
      <li className="nav-item dropdown" id="profile-menu">
        <a
          className="nav-link navbar-avatar"
          data-toggle="dropdown"
          aria-expanded="false"
          data-animation="scale-up"
          role="button"
        >
          <span
            className={
              props.avatarURI.error
                ? "avatar avatar-offline"
                : "avatar avatar-online"
            }
          >
            <img
              src={
                !props.avatarURI.error
                  ? props.avatarURI.URI ?? preloader
                  : errorImage
              }
              alt={authContext.userName}
            />
            <i></i>
          </span>
        </a>
        <div className="dropdown-menu" role="menu">
          <NavLink to="/auth/profile" className="dropdown-item" role="menuitem">
            <i className="icon ti-user" aria-hidden="true"></i>&nbsp;Profile
          </NavLink>
          <div className="dropdown-divider"></div>
          <button
            className="btn btn-link dropdown-item"
            role="menuitem"
            data-toggle="modal"
            data-target="#screenLockModal"
          >
            <i className="icon ti-lock" aria-hidden="true"></i>&nbsp;Lock screen
          </button>
          <button
            className="btn btn-link dropdown-item"
            role="menuitem"
            onClick={openAiModal}
          >
            <i className="icon ti-power-off" aria-hidden="true"></i>&nbsp;AI Switch
          </button>
          <button
            className="btn btn-link dropdown-item"
            role="menuitem"
            onClick={userSignOutHandler}
          >
            <i className="icon ti-power-off" aria-hidden="true"></i>&nbsp;Logout
          </button>
        </div>
      </li>

      {/* AI Settings Modal */}
      <Modal
        open={aiModalOpen}
        onClose={closeAiModal}
        aria-labelledby="ai-settings-modal"
        aria-describedby="ai-usage-control-settings"
      >
        <Paper elevation={3} sx={modalStyle}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h6" gutterBottom>
              AI Usage Control
            </Typography>
            
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip 
                icon={<BusinessIcon />} 
                label={authContext.companyName || authContext.companyCode || 'Company'} 
                variant="outlined" 
                size="small" 
              />
              <Chip 
                icon={authContext.isAdmin ? <AdminPanelSettingsIcon /> : <PersonIcon />} 
                label={authContext.userName || 'User'} 
                color={authContext.isAdmin ? 'primary' : 'default'}
                variant="outlined" 
                size="small" 
              />
            </Box>
            
            {authContext.isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={userSettingsEnabled}
                    onChange={handleUserSettingsToggle}
                    color="primary"
                  />
                }
                label="User-specific settings"
                sx={{ mb: 2 }}
              />
            )}
            
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              sx={{ 
                cursor: loading ? 'default' : 'pointer',
                opacity: loading ? 0.7 : 1,
                mb: 2 
              }}
              onClick={!loading ? handleToggle : undefined}
            >
              {isAiEnabled ? (
                <ToggleOnIcon sx={{ color: '#2e7d32', fontSize: 50 }} />
              ) : (
                <ToggleOffIcon sx={{ color: 'text.disabled', fontSize: 50 }} />
              )}
              
              <Box ml={2}>
                <Typography variant="body1" fontWeight="bold">
                  {loading ? 'Updating...' : `AI Mode ${isAiEnabled ? 'ON' : 'OFF'}`}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {scope === 'company' ? 'Company Setting' : 'User Setting'}
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {isAiEnabled 
                ? 'AI features are currently enabled. You can use all AI capabilities.' 
                : 'AI features are currently disabled. Some functionality may be limited.'}
            </Typography>
            
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, width: '100%' }}>
              <Button onClick={closeAiModal} variant="contained">
                Close
              </Button>
            </Box>
          </Box>
        </Paper>
      </Modal>
      
      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={3000} 
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarType} 
          onClose={() => setShowSnackbar(false)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    avatarURI: state.auth.avatarURI,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onSetAvatarURI: (URI) => dispatch({ type: "SET_AVATAR_URI", payload: URI }),
    onSetAvatarURIError: () => dispatch({ type: "SET_AVATAR_URI" }),
    onSignOut: () => dispatch({ type: "SIGN_OUT" }),
    onClearGeoInfo: () => dispatch({ type: "CLEAR_GEO_INFO" }),
    onClearTimeZones: () => dispatch({ type: "CLEAR_TIME_ZONES" }),
    onClearCompanies: () => dispatch({ type: "CLEAR_COMPANIES" }),
    onInstantiateGroups: (groups) =>
      dispatch({ type: "INSTANTIATE_USER_GROUPS", payload: groups }),
    onInstantiateGroupsError: () =>
      dispatch({ type: "INSTANTIATE_USER_GROUPS_ERROR" }),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(errorHandler(UserProfile, axios)));